import { Injectable } from "@angular/core";
import { Achievement, ACHIEVEMENTS } from "./achievements.data";
import { AppDataService } from "./app-data.service";
import { SampleGrouperSorter } from "./SampleGrouperSorter";
import { ToastController } from "@ionic/angular";

import { console } from "#/log";
import { filter, firstValueFrom, tap } from "rxjs";

/**
 * This services handles everything to do with updating, deleting, and completing achievements.
 */

@Injectable()
export class AchievementsService {
  numComplete: number;
  achievementsSet: boolean = false;

  constructor(
    private appData: AppDataService,
    private toastCtrl: ToastController,
  ) {
    this.numComplete = 0;
  }

  public async doInitialAchievementsOnce() {
    /** Initialize achievement data when local-data and progress is available */
    const [progress, groupedSamples] = await firstValueFrom(
      this.appData.getSamplesProgress().pipe(
        tap((progress) =>
          console.debug(
            `app-data initialAchievements progress updated`,
            progress,
          ),
        ),
        filter((samples) => typeof samples === "object"),
        tap((progress) =>
          console.debug(`app-data initialAchievements valid progress`, {
            progress,
          }),
        ),

        SampleGrouperSorter.groupSortWithProgress(),

        tap(([progress, groupedSamples]) =>
          console.debug(`app-data initialAchievements groupSortWithProgress`, {
            progress,
            groupedSamples,
          }),
        ),
      ),
    );
    await this.setInitialAchievements(progress, groupedSamples);
  }

  /**
   * Sets the initial state of achievements when the app is freshly opened.
   * It either loads from local storage or uses the local storage value.
   * Only local storage support for now.
   */
  async setInitialAchievements(
    samplesInProgress: any,
    groupedSamples: Record<string, any[]>,
  ): Promise<void> {
    console.debug("Setting initial achievements");

    // Reset and reinitialize achievements
    await this.appData.resetAchievements();

    const initialData = this.getAchievements();
    await this.appData.setAchievements(initialData);

    let beginnerCount = 0;
    let intermediateCount = 0;
    let tutorialCount = 0;

    // Gather sample keys for beginner and intermediate categories
    const beginnerSamples = (groupedSamples["Beginner"] || []).map(
      (s) => s["$key"],
    );
    const intermediateSamples = (groupedSamples["Intermediate"] || []).map(
      (s) => s["$key"],
    );

    // Count completed samples
    Object.entries(samplesInProgress).forEach(
      ([key, sample]: [string, any]) => {
        if (sample?.progress === 100) {
          if (beginnerSamples.includes(key)) {
            beginnerCount++;
          } else if (intermediateSamples.includes(key)) {
            intermediateCount++;
          } else if (key === "tutorial") {
            tutorialCount++;
          }
        }
      },
    );

    // Update achievements in parallel
    await Promise.all([
      this.increaseAllAchievements("Beginner", beginnerCount),
      this.increaseAllAchievements("Intermediate", intermediateCount),
      this.increaseAllAchievements(
        null,
        beginnerCount + intermediateCount + tutorialCount,
      ),
    ]);
  }

  /**
   * Gets the achievement ids
   */
  getAchievementIds(): Array<string> {
    var tmp: string[] = [];
    ACHIEVEMENTS.forEach((achievement) => {
      tmp.push(achievement.id);
    });
    return tmp;
  }

  /**
   * Returns all the achievements from file
   */
  getAchievements(): Array<Object> {
    var tmp: Object[] = [];
    ACHIEVEMENTS.forEach((achievement) => {
      tmp.push(achievement);
    });
    return tmp;
  }

  /**
   * Gets the achievements and whatever progress they have from local storage
   */
  getAchievementsFromLocalStorage(): Promise<Array<Object>> {
    let ids = this.getAchievementIds();
    console.log("achievements ids: ", ids);
    return this.appData.getLocalAchievements(ids).then((achievements) => {
      console.log("achievements: ", achievements);
      return Promise.resolve(achievements);
    });
  }

  /**
   * TODO: modulate this and increaseAchievement, they use a lot of similar pieces
   * Increases achievements by multiple steps at once, for when the app is freshly opened.
   * This is because achievements are only stored locally and get wiped between updates.
   * @param achievementType - the type of achievement as a string, either is beginner, intermediate, or none (for count achievements)
   * @param progressToSet - the amount of progress to set, as a count of the number of items, not a percentage (ie beginner would be 0-14, etc)
   */
  increaseAllAchievements(
    achievementType: string | null,
    progressToSet: number,
  ): Promise<void> {
    const ids = this.getAchievementIds();
    return this.appData
      .getLocalAchievements(ids)
      .then((data: Achievement[]) => {
        this.numComplete++;

        if (achievementType === "Beginner") {
          const beginnerAch = data.find(
            (achieve) => achieve.keyword === "beginner",
          );
          if (beginnerAch && !beginnerAch.completed) {
            const percentToSet = Math.round(
              (progressToSet / beginnerAch.outOf) * 100,
            );

            beginnerAch.progress = progressToSet;
            beginnerAch.percent = percentToSet;
            beginnerAch.completed = percentToSet === 100;

            return this.appData
              .updateAchievements(beginnerAch, percentToSet, progressToSet)
              .then(() => Promise.resolve());
          }
        } else if (achievementType === "Intermediate") {
          const intermediateAch = data.find(
            (achieve) => achieve.keyword === "intermediate",
          );
          if (intermediateAch && !intermediateAch.completed) {
            const percentToSet = Math.round(
              (progressToSet / intermediateAch.outOf) * 100,
            );

            intermediateAch.progress = progressToSet;
            intermediateAch.percent = percentToSet;
            intermediateAch.completed = percentToSet === 100;

            return this.appData
              .updateAchievements(intermediateAch, percentToSet, progressToSet)
              .then(() => Promise.resolve());
          }
        } else {
          // Handle count-based achievements ("single", "five")
          const promises: Promise<void>[] = [];
          const achievementSubset = data.filter(
            (achieve) =>
              achieve.keyword === "single" || achieve.keyword === "five",
          );

          for (const achievement of achievementSubset) {
            if (!achievement.completed) {
              let percentToSet = Math.round(
                (progressToSet / achievement.outOf) * 100,
              );
              percentToSet = Math.min(percentToSet, 100);

              achievement.progress = Math.min(progressToSet, achievement.outOf);
              achievement.percent = percentToSet;
              achievement.completed = percentToSet === 100;

              promises.push(
                this.appData
                  .updateAchievements(achievement, percentToSet, progressToSet)
                  .then(() => Promise.resolve()),
              );
            }
          }

          return Promise.all(promises).then(() => Promise.resolve());
        }

        return Promise.resolve();
      });
  }

  /**
   * Increases the given achievement, and will mark an achievement as cleared if its percentage reaches 100%
   */
  increaseAchievement(category: string | null = null) {
    const ids = this.getAchievementIds();
    this.appData.getLocalAchievements(ids).then((rawData) => {
      const data = rawData as Achievement[];
      this.numComplete++;

      const incrementAchievement = (achievement: Achievement) => {
        const currentProgress =
          typeof achievement.progress === "number" ? achievement.progress : 0;
        const newProgress = Math.min(currentProgress + 1, achievement.outOf);
        const percentToSet = Math.round(
          (newProgress / achievement.outOf) * 100,
        );

        achievement.progress = newProgress;
        achievement.percent = percentToSet;
        achievement.completed = percentToSet === 100;

        console.debug(
          `${achievement.keyword} achievement - updated:`,
          achievement,
        );

        this.appData
          .updateAchievements(achievement, percentToSet, newProgress)
          .then((didCompleteAchievement) => {
            if (didCompleteAchievement) {
              this.showUnlockedAchievement(achievement);
            }
          });
      };

      if (category === "Beginner") {
        const beginnerAch = data.find((ach) => ach.keyword === "beginner");
        if (beginnerAch && !beginnerAch.completed) {
          incrementAchievement(beginnerAch);
        }
      } else if (category === "Intermediate") {
        const intermediateAch = data.find(
          (ach) => ach.keyword === "intermediate",
        );
        if (intermediateAch && !intermediateAch.completed) {
          incrementAchievement(intermediateAch);
        }
      }

      // Handle general count-based achievements
      const achievementSubset = data.filter(
        (ach) => ach.keyword === "single" || ach.keyword === "five",
      );

      for (const achievement of achievementSubset) {
        if (!achievement.completed) {
          incrementAchievement(achievement);
        }
      }
    });
  }

  /**
   * Calculates how much to increase the given achievement by
   * @param category
   * @returns the percent of the given achievement's completion
   */
  calculateAchievement(achievement: Achievement | undefined): number {
    if (!achievement) {
      console.warn("Achievement is undefined");
      return 0;
    }

    const rawProgress = achievement.progress ?? 0;
    const progress = rawProgress === 0 ? 1 : rawProgress;
    const outOf = achievement.outOf ?? 1; // Prevent divide-by-zero

    const newPercentage = progress >= outOf ? 100 : (progress / outOf) * 100;
    return Math.round(newPercentage);
  }

  /**
   * Displays a toast on the screen when an achievement has been successfully unlocked
   * @param unlockedAchievement the achievement to display
   */

  async showUnlockedAchievement(unlockedAchievement: Achievement | undefined) {
    if (!unlockedAchievement) {
      console.warn("showUnlockedAchievement called with undefined achievement");
      return;
    }

    let toastCss = "";
    switch (unlockedAchievement.keyword) {
      case "single":
        toastCss = "oneAchievementToast";
        break;
      case "five":
        toastCss = "fiveAchievementToast";
        break;
      case "beginner":
        toastCss = "beginnerAchievementToast";
        break;
      case "intermediate":
        toastCss = "intermediateAchievementToast";
        break;
      default:
        toastCss = "";
        break;
    }

    const toast = await this.toastCtrl.create({
      message: "Achievement Unlocked - " + unlockedAchievement.title,
      position: "bottom",
      duration: 6000,
      cssClass: toastCss,
      buttons: [
        {
          text: "Close",
          role: "cancel",
        },
      ],
    });
  }
}
